import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from '../router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Hearing,
  Mic,
  MicOff,
  Radio,
  Videocam,
  VideocamOff,
  Wifi,
} from '@mui/icons-material';
import { Room, RoomEvent } from 'livekit-client';
import ParticipantMedia from '../live/ParticipantMedia';
import { browserLivekitUrlCandidates } from '../live/livekitUrl';
import { enumerateStudioDevices } from '../live/mediaDevices';
import liveStudioApi, { getLiveStudioError } from '../live/liveStudioApi';
import GuestRundown from '../live/GuestRundown';
import ColorModeToggle from '../components/ColorModeToggle';

const credentialsFrom = (payload) => ({
  livekitUrl:
    payload?.livekitUrl ||
    payload?.url ||
    payload?.room?.livekitUrl ||
    payload?.credentials?.livekitUrl,
  token:
    payload?.token ||
    payload?.accessToken ||
    payload?.room?.token ||
    payload?.credentials?.token,
});

const invitationFrom = (payload) => payload?.invite || payload || {};
const emptyDeviceHint = () => (
  typeof window !== 'undefined' && window.isSecureContext === false
    ? 'Needs HTTPS'
    : 'Allow device access'
);

export default function LiveGuest() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [roomClient, setRoomClient] = useState(null);
  const [purpose, setPurpose] = useState('live');
  const [participants, setParticipants] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState({ audioInputs: [], videoInputs: [], audioOutputs: [] });
  const [audioInputId, setAudioInputId] = useState('');
  const [videoInputId, setVideoInputId] = useState('');
  const [audioOutputId, setAudioOutputId] = useState('');
  const [previewActive, setPreviewActive] = useState(false);
  const roomRef = useRef(null);
  const previewRef = useRef(null);
  const joinInFlightRef = useRef(false);
  const previewVideoRef = useRef(null);

  const stopPreview = useCallback(() => {
    previewRef.current?.getTracks?.().forEach((track) => track.stop());
    previewRef.current = null;
    if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    setPreviewActive(false);
  }, []);

  const applyDeviceList = useCallback((next) => {
    setDevices(next);
    setAudioInputId((current) =>
      current && next.audioInputs.some((device) => device.deviceId === current)
        ? current
        : next.audioInputs[0]?.deviceId || ''
    );
    setVideoInputId((current) =>
      current && next.videoInputs.some((device) => device.deviceId === current)
        ? current
        : next.videoInputs[0]?.deviceId || ''
    );
    setAudioOutputId((current) =>
      current && next.audioOutputs.some((device) => device.deviceId === current)
        ? current
        : next.audioOutputs[0]?.deviceId || ''
    );
  }, []);

  const refreshDevices = useCallback(async ({ allowProbe = false } = {}) => {
    const mediaInUse = Boolean(previewRef.current) || Boolean(roomRef.current);
    const next = await enumerateStudioDevices({
      probeAudio: true,
      probeVideo: true,
      allowProbe: allowProbe && !mediaInUse,
    });
    applyDeviceList(next);
  }, [applyDeviceList]);

  useEffect(() => {
    refreshDevices({ allowProbe: true }).catch(() => {});
    const onDeviceChange = () => refreshDevices({ allowProbe: true }).catch(() => {});
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
  }, [refreshDevices]);

  const syncParticipants = useCallback((room = roomRef.current) => {
    if (!room) {
      setParticipants([]);
      return;
    }
    setParticipants([
      room.localParticipant,
      ...Array.from(room.remoteParticipants.values()).slice(0, 6),
    ]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadInvite = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await liveStudioApi.getInvite(token);
        const invite = invitationFrom(payload);
        if (!cancelled) {
          setInvitation(invite);
          setDisplayName(invite.displayName || invite.display_name || '');
          setPurpose(invite.purpose === 'voicetrack' || invite.invite_type === 'voicetrack'
            ? 'voicetrack'
            : 'live');
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(getLiveStudioError(requestError, 'This studio invitation is invalid or expired.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => () => {
    stopPreview();
    const current = roomRef.current;
    roomRef.current = null;
    if (current) Promise.resolve(current.disconnect()).catch(() => {});
  }, [stopPreview]);

  const startPreview = async () => {
    setError('');
    stopPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioInputId ? { deviceId: { exact: audioInputId } } : true,
        video: videoInputId ? { deviceId: { exact: videoInputId } } : true,
      });
      previewRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        await previewVideoRef.current.play().catch(() => {});
      }
      setPreviewActive(true);
      await refreshDevices();
    } catch (previewError) {
      setError(getLiveStudioError(previewError, 'Camera or microphone preview was denied.'));
    }
  };

  const joinRoom = async () => {
    if (joinInFlightRef.current) return;
    if (!displayName.trim()) {
      setError('Enter your display name.');
      return;
    }
    joinInFlightRef.current = true;
    setJoining(true);
    setError('');
    try {
      const payload = await liveStudioApi.joinInvite(token, displayName.trim());
      const credentials = credentialsFrom(payload);
      if (payload?.purpose) setPurpose(payload.purpose === 'voicetrack' ? 'voicetrack' : 'live');
      if (!credentials.livekitUrl || !credentials.token) {
        throw new Error('The invitation did not provide room credentials.');
      }

      stopPreview();
      const candidates = browserLivekitUrlCandidates(credentials.livekitUrl);
      if (!candidates.length) {
        throw new Error('The invitation did not provide a reachable LiveKit signaling URL.');
      }
      let room = null;
      let lastError = null;
      let settled = false;
      const bindRoom = (nextRoom) => {
        const sync = () => {
          if (roomRef.current === nextRoom) syncParticipants(nextRoom);
        };
        [
          RoomEvent.ParticipantConnected,
          RoomEvent.ParticipantDisconnected,
          RoomEvent.TrackSubscribed,
          RoomEvent.TrackUnsubscribed,
          RoomEvent.TrackMuted,
          RoomEvent.TrackUnmuted,
          RoomEvent.LocalTrackPublished,
          RoomEvent.LocalTrackUnpublished,
          RoomEvent.ActiveSpeakersChanged,
        ].forEach((eventName) => nextRoom.on(eventName, sync));
        nextRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
          if (!settled || roomRef.current !== nextRoom) return;
          setConnectionState(String(state));
        });
        nextRoom.on(RoomEvent.Disconnected, () => {
          if (!settled || roomRef.current !== nextRoom) return;
          setConnectionState('disconnected');
        });
        return sync;
      };
      for (const url of candidates) {
        if (room) await Promise.resolve(room.disconnect()).catch(() => {});
        room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;
        setRoomClient(room);
        setConnectionState('connecting');
        const sync = bindRoom(room);
        try {
          await room.connect(url, credentials.token);
          lastError = null;
          settled = true;
          if (audioOutputId && typeof room.switchActiveDevice === 'function') {
            await room.switchActiveDevice('audiooutput', audioOutputId).catch(() => {});
          }
          setConnectionState('connected');
          sync();
          break;
        } catch (connectError) {
          lastError = connectError;
        }
      }
      if (lastError) {
        throw new Error(`${lastError.message || lastError} Tried ${candidates.join(', ')}`);
      }
    } catch (joinError) {
      const current = roomRef.current;
      roomRef.current = null;
      setRoomClient(null);
      if (current) await Promise.resolve(current.disconnect()).catch(() => {});
      setError(getLiveStudioError(joinError, 'Could not join the studio.'));
      setConnectionState('disconnected');
    } finally {
      joinInFlightRef.current = false;
      setJoining(false);
    }
  };

  const toggleMicrophone = async () => {
    try {
      const local = roomClient?.localParticipant;
      if (!local?.isMicrophoneEnabled && audioInputId && roomClient?.switchActiveDevice) {
        await roomClient.switchActiveDevice('audioinput', audioInputId);
      }
      await local?.setMicrophoneEnabled(!local.isMicrophoneEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      setError(getLiveStudioError(mediaError, 'Microphone access was denied.'));
    }
  };

  const toggleCamera = async () => {
    try {
      const local = roomClient?.localParticipant;
      if (!local?.isCameraEnabled && videoInputId && roomClient?.switchActiveDevice) {
        await roomClient.switchActiveDevice('videoinput', videoInputId);
      }
      await local?.setCameraEnabled(!local.isCameraEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      setError(getLiveStudioError(mediaError, 'Camera access was denied.'));
    }
  };

  const leaveRoom = async () => {
    const current = roomRef.current;
    roomRef.current = null;
    if (current) await Promise.resolve(current.disconnect()).catch(() => {});
    setRoomClient(null);
    setParticipants([]);
    setConnectionState('disconnected');
  };

  useEffect(() => {
    if (!roomClient || !audioOutputId || typeof roomClient.switchActiveDevice !== 'function') return;
    roomClient.switchActiveDevice('audiooutput', audioOutputId).catch(() => {
      setError('The selected studio output is no longer available.');
    });
  }, [audioOutputId, roomClient]);

  const connected = connectionState === 'connected';
  const joined = Boolean(roomClient);
  const localParticipant = roomClient?.localParticipant;
  const isVoicetrack = purpose === 'voicetrack';
  const pageTitle = isVoicetrack ? 'Voicetrack session' : 'Live Studio guest';

  return (
    <Box
      sx={{
        minHeight: 'var(--rs-vh)',
        p: { xs: 1.5, md: 3 },
        display: 'grid',
        placeItems: 'center',
        background: (theme) => theme.palette.mode === 'dark'
          ? 'radial-gradient(900px 520px at 15% 0%, rgba(34,211,238,0.14), transparent 60%), var(--rs-bg)'
          : 'radial-gradient(900px 520px at 15% 0%, rgba(34,211,238,0.12), transparent 60%), var(--rs-bg)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: joined || isVoicetrack ? 1180 : 560 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(34,211,238,0.13)',
              color: 'var(--rs-accent)',
              border: '1px solid rgba(34,211,238,0.24)',
            }}
          >
            <Radio />
          </Box>
          <Box>
            <Typography variant="h5">{pageTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {isVoicetrack ? 'Record a voicetrack with the presenter' : 'Secure contribution link'}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <ColorModeToggle />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Checking invitation…</Typography>
            </CardContent>
          </Card>
        ) : !joined ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isVoicetrack ? '1fr 1fr' : '1fr' }, gap: 2 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: 3 } }}>
              <Chip
                size="small"
                label={isVoicetrack ? 'Invited co-presenter' : 'Invited guest'}
                color="info"
                sx={{ mb: 2 }}
              />
              <Typography variant="h5" sx={{ mb: 0.75 }}>
                Join {invitation?.roomName || invitation?.station_name || invitation?.room?.name || (isVoicetrack ? 'the session' : 'the live room')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isVoicetrack
                  ? 'You will record a voicetrack together with the presenter. Your microphone and camera stay off until you enable them.'
                  : 'Your microphone and camera remain off until you enable them after joining.'}
              </Typography>
              <TextField
                fullWidth
                label="Display name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !joining) joinRoom();
                }}
                inputProps={{ maxLength: 80 }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Microphone</InputLabel>
                  <Select
                    label="Microphone"
                    value={audioInputId}
                    onChange={(event) => setAudioInputId(event.target.value)}
                    displayEmpty
                  >
                    {devices.audioInputs.length === 0 && (
                      <MenuItem value="">{emptyDeviceHint()}</MenuItem>
                    )}
                    {devices.audioInputs.map((device, index) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${index + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Camera</InputLabel>
                  <Select
                    label="Camera"
                    value={videoInputId}
                    onChange={(event) => setVideoInputId(event.target.value)}
                    displayEmpty
                  >
                    {devices.videoInputs.length === 0 && (
                      <MenuItem value="">{emptyDeviceHint()}</MenuItem>
                    )}
                    {devices.videoInputs.map((device, index) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${index + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <InputLabel>Studio audio output</InputLabel>
                  <Select
                    label="Studio audio output"
                    value={audioOutputId}
                    onChange={(event) => setAudioOutputId(event.target.value)}
                    displayEmpty
                  >
                    {devices.audioOutputs.length === 0 && (
                      <MenuItem value="">{emptyDeviceHint()}</MenuItem>
                    )}
                    {devices.audioOutputs.map((device, index) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Output ${index + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                component="video"
                ref={previewVideoRef}
                autoPlay
                playsInline
                muted
                sx={{
                  display: previewActive ? 'block' : 'none',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  objectFit: 'cover',
                  bgcolor: '#05080d',
                  borderRadius: 2,
                  mb: 1.5,
                }}
              />
              <Button
                fullWidth
                variant="outlined"
                startIcon={previewActive ? <VideocamOff /> : <Videocam />}
                onClick={previewActive ? stopPreview : startPreview}
                sx={{ mb: 1.25 }}
              >
                {previewActive ? 'Stop device preview' : 'Preview camera and microphone'}
              </Button>
              {invitation?.expires_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                  Link expires {new Date(invitation.expires_at).toLocaleString()}.
                </Typography>
              )}
              <Button
                fullWidth
                size="large"
                variant="contained"
                disabled={joining || !invitation}
                startIcon={joining ? <CircularProgress size={18} color="inherit" /> : <Wifi />}
                onClick={joinRoom}
              >
                {joining ? 'Joining…' : (isVoicetrack ? 'Join the session' : 'Join studio')}
              </Button>
            </CardContent>
          </Card>
          {isVoicetrack && (
            <GuestRundown token={token} api={liveStudioApi} />
          )}
          </Box>
        ) : (
          <Card sx={{ bgcolor: 'var(--rs-paper)', overflow: 'hidden' }}>
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                borderBottom: '1px solid rgba(148,163,184,0.14)',
              }}
            >
              <Box>
                <Typography variant="subtitle1">
                  {invitation?.roomName || invitation?.station_name || 'Live Studio'}
                </Typography>
                <Chip
                  size="small"
                  label={connectionState}
                  color={connected ? 'success' : 'warning'}
                  sx={{ height: 20, textTransform: 'capitalize' }}
                />
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Hearing />}
                  disabled={!connected}
                  onClick={() => Promise.resolve(roomClient?.startAudio?.())
                    .catch(() => setError('Browser audio playback is blocked.'))}
                >
                  Hear studio
                </Button>
                <Button
                  size="small"
                  variant={localParticipant?.isMicrophoneEnabled ? 'contained' : 'outlined'}
                  color={localParticipant?.isMicrophoneEnabled ? 'success' : 'inherit'}
                  startIcon={localParticipant?.isMicrophoneEnabled ? <Mic /> : <MicOff />}
                  onClick={toggleMicrophone}
                  disabled={!connected}
                >
                  Microphone
                </Button>
                <Button
                  size="small"
                  variant={localParticipant?.isCameraEnabled ? 'contained' : 'outlined'}
                  color={localParticipant?.isCameraEnabled ? 'info' : 'inherit'}
                  startIcon={localParticipant?.isCameraEnabled ? <Videocam /> : <VideocamOff />}
                  onClick={toggleCamera}
                  disabled={!connected}
                >
                  Camera
                </Button>
                <Button size="small" variant="outlined" color="error" onClick={leaveRoom}>
                  Leave
                </Button>
              </Stack>
            </Box>
            <CardContent>
              {isVoicetrack ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 1.5 }}>
                  <ParticipantMedia
                    participants={participants}
                    localParticipant={localParticipant}
                    audioOutputId={audioOutputId}
                  />
                  <GuestRundown token={token} api={liveStudioApi} />
                </Box>
              ) : (
                <ParticipantMedia
                  participants={participants}
                  localParticipant={localParticipant}
                  audioOutputId={audioOutputId}
                />
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}
